import { useMemo, useState, useEffect } from 'react';
import { css } from '@emotion/react';
import { isEqual } from 'lodash-es';
import { ExceptionTypes, ExceptionUtils } from '@frontend/api-schedule-exceptions';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Table,
  TableColumnConfig,
  Text,
  TableFilters,
  useForm,
  TimeRangeField,
  DateRangeField,
  CheckboxField,
  TextField,
  ModalControlModalProps,
} from '@frontend/design-system';
import { useScheduleSettingsSmallMode } from '../../hooks/settings/use-schedule-settings-small-mode';
import { CompactTable } from '../compact-table';
import { ExceptionAvailabilityCellValue, ExceptionTimeCellValue } from './exceptions-table-cell-values';
import { ScheduleExceptionsExpandedRow } from './schedule-excpetions-expanded-row';

interface Props {
  exceptionData?: ExceptionTypes.ExceptionData[];
  handleEdit: (id: string) => void;
  handleDelete: (id: string) => void;
  filters: ExceptionTypes.ExceptionTableFilters;
  handleFiltersUpdate: (newFilters: ExceptionTypes.ExceptionTableFilters) => void;
  isLoading: boolean;
}

export function ScheduleExceptionsTable({
  exceptionData,
  handleEdit,
  handleDelete,
  filters,
  handleFiltersUpdate,
  isLoading,
}: Props) {
  const { t } = useTranslation('schedule');
  const { isSmallMode } = useScheduleSettingsSmallMode();
  const { convertTimeForComparison, getDisplayDate, defaultFilterObject } = ExceptionUtils;
  const [searchValue, setSearchValue] = useState<string>('');
  const [filteredData, setFilteredData] = useState<ExceptionTypes.ExceptionData[]>();

  useEffect(() => {
    const data = exceptionData?.filter((item) => item?.name?.toLowerCase().includes(searchValue.toLowerCase())) || [];
    setFilteredData(data);
  }, [searchValue, exceptionData]);

  const filterByName = (exceptions: ExceptionTypes.ExceptionData[]) => {
    return exceptions?.filter((item) => item?.name?.toLowerCase().includes(filters.name?.toLowerCase() || '')) || [];
  };

  const filerByAvailability = (exceptions: ExceptionTypes.ExceptionData[]) => {
    if (!filters.isAvailable && !filters.isUnavailable) {
      return exceptions;
    }
    if (filters.isAvailable && filters.isUnavailable) {
      return exceptions;
    }
    if (filters.isAvailable && !filters.isUnavailable) {
      return exceptions?.filter((item) => item?.isAvailable.includes(filters.isAvailable.toString())) || [];
    }
    if (filters.isUnavailable && !filters.isAvailable) {
      return exceptions?.filter((item) => item?.isAvailable === 'false') || [];
    }
    return;
  };

  const filterByTime = (exceptions: ExceptionTypes.ExceptionData[]) => {
    if (filters.isAllDay) {
      return exceptions?.filter((item) => item?.startTime === '12:00am' && item.endTime === '11:59pm') || [];
    }
    if (filters.timeRange[0].length && !filters.isAllDay) {
      return (
        exceptions?.filter(
          (item) =>
            convertTimeForComparison(item?.startTime) >= convertTimeForComparison(filters.timeRange[0]) &&
            convertTimeForComparison(item?.endTime) <= convertTimeForComparison(filters.timeRange[1])
        ) || []
      );
    } else {
      return exceptions;
    }
  };

  useEffect(() => {
    let result = exceptionData;
    result = filerByAvailability(result || []);
    result = filterByName(result || []);
    result = filterByTime(result);
    setFilteredData(result);
  }, [filters]);

  const tableColumns = useMemo(
    (): TableColumnConfig<ExceptionTypes.ExceptionData>[] => [
      {
        Header: t('Name'),
        id: 'name',
        accessor: (row) => <Text as='span'>{row.name}</Text>,
        disableSortBy: true,
        minWidth: 100,
      },
      {
        Header: t('Availability'),
        id: 'available',
        accessor: (row) => <ExceptionAvailabilityCellValue isAvailable={row.isAvailable} />,
        disableSortBy: true,
        width: 150,
        minWidth: 100,
      },
      {
        Header: t('Date'),
        id: 'date',
        accessor: (row) => (
          <Text as='span' weight='semibold'>
            {getDisplayDate(row.startDate, row.endDate)}
          </Text>
        ),
        disableSortBy: true,
      },
      {
        Header: t('Time'),
        id: 'time',
        accessor: (row) => <ExceptionTimeCellValue startTime={row.startTime} endTime={row.endTime} />,
        disableSortBy: true,
      },
    ],
    [exceptionData, filteredData]
  );

  const rowActionMenu = useMemo(
    () => [
      {
        Icon: () => <Icon name='edit' />,
        label: t('Edit'),
        onClick: (row: ExceptionTypes.ExceptionData) => handleEdit(row.id),
      },
      {
        Icon: () => <Icon name='trash' />,
        label: t('Delete'),
        onClick: (row: ExceptionTypes.ExceptionData) => handleDelete(row.id),
      },
    ],
    [handleDelete, handleEdit, t]
  );

  const commonTableConfigs = {
    data: filteredData ?? [],
    emptyStateConfig: {
      type: 'schedule' as const,
      header: t('No exception data to display'),
    },
    rowActions: {
      actions: rowActionMenu,
    },
    globalSearchConfig: {
      searchHandler: setSearchValue,
      placeholder: t('Search by Name'),
      debounceDelay: 500,
    },
    manualFiltersRender: (
      modalProps: ModalControlModalProps,
      setShowNotification: (showNotification: boolean) => void
    ) => (
      <ExceptionsTableFilters
        modalProps={modalProps}
        setShowNotificationBadge={setShowNotification}
        defaultFilters={defaultFilterObject}
        filters={filters}
        handleFiltersUpdate={handleFiltersUpdate}
      />
    ),
  };

  return isSmallMode ? (
    <CompactTable
      tableId='schedule-exceptions-v2-compact-table'
      rowElement={(row: ExceptionTypes.ExceptionData) => <CompactTable.RowHeader title={row.name} />}
      expandedRowElement={(row: ExceptionTypes.ExceptionData) => <ScheduleExceptionsExpandedRow exceptionData={row} />}
      toolbarElement={<CompactTable.Toolbar />}
      isLoading={isLoading}
      {...commonTableConfigs}
    />
  ) : (
    <Table
      hasResponsiveColWidths
      hasGlobalSearch
      isPaginated
      colConfig={tableColumns}
      manualFilters
      manualSortBy
      {...commonTableConfigs}
    />
  );
}

type ExceptionsTableFiltersProps = {
  modalProps: ModalControlModalProps;
  handleFiltersUpdate: (filters: ExceptionTypes.ExceptionTableFilters) => void;
  setShowNotificationBadge: (showNotification: boolean) => void;
  defaultFilters: ExceptionTypes.ExceptionTableFilters;
  filters: ExceptionTypes.ExceptionTableFilters;
};

const ExceptionsTableFilters = ({
  modalProps,
  defaultFilters,
  filters,
  setShowNotificationBadge,
  handleFiltersUpdate,
}: ExceptionsTableFiltersProps) => {
  const { t } = useTranslation('schedule');

  const { getFieldProps, seedValues, values, formProps } = useForm({
    fields: {
      name: { type: 'text', value: filters.name },
      isAvailable: { type: 'checkbox', value: filters.isAvailable },
      isUnavailable: { type: 'checkbox', value: filters.isUnavailable },
      dateRange: {
        type: 'dateRange',
        value: filters.dateRange,
      },
      timeRange: {
        type: 'timeRange',
        value: filters.timeRange,
        interval: 15,
      },
      isAllDay: { type: 'checkbox', value: filters.isAllDay },
    },
    onSubmit: (values) => {
      handleFiltersUpdate(values as ExceptionTypes.ExceptionTableFilters);
    },
  });

  return (
    <TableFilters
      {...modalProps}
      formProps={formProps}
      onApplyClick={() => {
        handleFiltersUpdate(values as ExceptionTypes.ExceptionTableFilters);
        setShowNotificationBadge(!isEqual(defaultFilters, values));
      }}
      onClearAllClick={() => {
        seedValues(defaultFilters);
        handleFiltersUpdate(defaultFilters);
        setShowNotificationBadge(false);
      }}
    >
      <TableFilters.Section sectionHeaderLabel={t('Name of Exception')}>
        <TextField {...getFieldProps('name')} label={t('Name of Exception')} />
      </TableFilters.Section>
      <TableFilters.Section sectionHeaderLabel={t('Select Availability')}>
        <CheckboxField
          {...getFieldProps('isUnavailable')}
          label={t('Unavailable')}
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
        />
        <CheckboxField {...getFieldProps('isAvailable')} label={t('Available')} />
      </TableFilters.Section>
      <TableFilters.Section sectionHeaderLabel={t('Select Date Range')}>
        <DateRangeField {...getFieldProps('dateRange')} label={t('Start and end dates')}></DateRangeField>
      </TableFilters.Section>
      <TableFilters.Section sectionHeaderLabel={t('Select Time Range')}>
        <CheckboxField
          {...getFieldProps('isAllDay')}
          label={t('All Day')}
          css={css`
            margin-bottom: ${theme.spacing(2)};
          `}
        />
        {!getFieldProps('isAllDay').value && (
          <TimeRangeField {...getFieldProps('timeRange')} label={t('Start and end times')}></TimeRangeField>
        )}
      </TableFilters.Section>
    </TableFilters>
  );
};
